import { join } from "node:path";
import { Brand, CARRIER_BRAND } from "./brand.js";
import { exists, readFile } from "./fsx.js";
import { runInherit, which } from "./exec.js";
import {
  buildScriptFor,
  configuredProjectName,
  deployTo,
  probeAll,
  putSecret,
  rankTargets,
  setCustomDomain,
  stageSecrets,
  type StagedSecrets,
  type TargetId,
  type TargetStatus,
} from "./deploy-targets.js";
import { discoverSecrets, RUNTIME_SECRET_KEYS } from "./storefront-secrets.js";

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "storefront";
}

/** Install storefront deps. Uses npm to stay independent of the pnpm workspace. */
export async function installDeps(target: string): Promise<boolean> {
  const pkgMgr = (await which("pnpm")) ? "pnpm" : "npm";
  const r = await runInherit(pkgMgr, ["install"], { cwd: target });
  return r.ok;
}

/**
 * Build the storefront for a deploy target.
 *
 * Cloudflare needs `cf:build`, which runs `next build` through OpenNext and
 * emits the `.open-next/worker.js` bundle. Every other target ships the plain
 * Next output, so they use `build`.
 */
export async function buildSite(target: string, forTarget: TargetId = "cloudflare"): Promise<boolean> {
  const pkgMgr = (await which("pnpm")) ? "pnpm" : "npm";
  const r = await runInherit(pkgMgr, ["run", buildScriptFor(forTarget)], { cwd: target });
  return r.ok;
}

/** Read white-label name from a scaffolded storefront (falls back to Carrier defaults). */
export async function loadStorefrontBrand(
  target: string,
  overrides?: { name?: string },
): Promise<Brand> {
  const configPath = join(target, "src", "brand.config.ts");
  if (!(await exists(configPath))) {
    return { ...CARRIER_BRAND, ...overrides };
  }
  const src = await readFile(configPath, "utf8");
  const pick = (field: string, fallback: string): string => {
    const m = src.match(new RegExp(`\\b${field}:\\s*(?:[^"\\n]*\\?\\?\\s*)?"([^"]*)"`));
    return m?.[1] ?? fallback;
  };
  return {
    ...CARRIER_BRAND,
    name: overrides?.name ?? pick("name", CARRIER_BRAND.name),
    domain: pick("domain", CARRIER_BRAND.domain),
    supportEmail: pick("supportEmail", CARRIER_BRAND.supportEmail),
    supportUrl: pick("supportUrl", CARRIER_BRAND.supportUrl),
    tagline: pick("tagline", CARRIER_BRAND.tagline),
    legalName: pick("legalName", CARRIER_BRAND.legalName),
    colors: {
      ...CARRIER_BRAND.colors,
      accent: pick("accent", CARRIER_BRAND.colors.accent),
      accentDark: pick("accentDark", CARRIER_BRAND.colors.accentDark),
      bg: pick("bg", CARRIER_BRAND.colors.bg),
      text: pick("text", CARRIER_BRAND.colors.text),
    },
    carrierApiUrl: CARRIER_BRAND.carrierApiUrl,
  };
}

/**
 * Deploy the storefront to whichever target is available on this box.
 *
 * `preferred` forces one target and fails loudly if it is not ready, because a
 * silent fallback to a different host is not something a deploy should decide.
 * With no preference, the ranked list wins: a target the project is already
 * configured for, else Cloudflare, which is what the template is built for.
 *
 * Runtime secrets are pushed after a successful deploy — see `pushSecrets`. The
 * previous Cloudflare-only path never did this, which is why a deployed
 * storefront returned 500 on every page that reads the catalog.
 */
export async function deploySite(
  target: string,
  brand: Brand,
  opts: {
    preferred?: TargetId;
    pushSecrets?: boolean;
    env?: Record<string, string | undefined>;
    customDomain?: boolean;
  } = {},
): Promise<DeploySiteResult> {
  const statuses = await probeAll(target);

  let chosen: TargetStatus | undefined;
  if (opts.preferred) {
    const wanted = statuses.find((s) => s.id === opts.preferred);
    if (!wanted?.ready) {
      return {
        ok: false,
        projectName: slug(brand.name),
        statuses,
        reason:
          wanted?.reason ??
          `${opts.preferred} is not available on this machine.`,
      };
    }
    chosen = wanted;
  } else {
    chosen = rankTargets(statuses)[0];
  }

  if (!chosen) {
    return {
      ok: false,
      projectName: slug(brand.name),
      statuses,
      reason:
        "No deploy target is ready. " +
        statuses.map((s) => `${s.label}: ${s.reason ?? "unavailable"}`).join(" | "),
    };
  }

  // The project's own config names the deployment. Falling back to the brand
  // slug would deploy a second Worker and leave the real one untouched.
  const projectName = (await configuredProjectName(chosen.id, target)) ?? slug(brand.name);

  // Cloudflare provisions DNS and the certificate itself when the route is
  // marked as a custom domain, so wire it before deploying.
  if (opts.customDomain && chosen.id === "cloudflare" && brand.domain) {
    await setCustomDomain(target, brand.domain);
  }

  // Secrets go in place BEFORE the code, so there is never a window where the
  // new deployment is live without them.
  let staged: StagedSecrets = { staged: [], failed: [], cleanup: async () => {} };
  if (opts.pushSecrets !== false) {
    const discovered = await discoverSecrets(target, opts.env ?? process.env);
    const runtime: Record<string, string> = {};
    for (const key of RUNTIME_SECRET_KEYS) {
      const value = discovered[key];
      if (value) runtime[key] = value;
    }
    staged = await stageSecrets(chosen.id, target, projectName, runtime);
  }

  try {
    const outcome = await deployTo(chosen.id, target, projectName, {
      secretsFile: staged.secretsFile,
    });
    if (!outcome.ok) {
      return { ok: false, projectName, statuses, target: chosen.id, reason: outcome.reason };
    }
    return {
      ok: true,
      projectName,
      statuses,
      target: chosen.id,
      url: outcome.url,
      secrets: { pushed: staged.staged, failed: staged.failed },
    };
  } finally {
    await staged.cleanup();
  }
}

export interface DeploySiteResult {
  ok: boolean;
  projectName: string;
  statuses: TargetStatus[];
  target?: TargetId;
  url?: string;
  reason?: string;
  secrets?: { pushed: string[]; failed: string[] };
}

/**
 * Push the server-side secrets a deployed storefront needs.
 *
 * Only keys that actually resolve to a value are pushed; a missing key is left
 * to the caller to report, because inventing one would be worse than a clear gap.
 */
export async function pushSecrets(
  targetId: TargetId,
  storefront: string,
  projectName: string,
  env: Record<string, string | undefined> = process.env,
): Promise<{ pushed: string[]; failed: string[] }> {
  const secrets = await discoverSecrets(storefront, env);
  const pushed: string[] = [];
  const failed: string[] = [];
  for (const key of RUNTIME_SECRET_KEYS) {
    const value = secrets[key];
    if (!value) continue;
    const ok = await putSecret(targetId, storefront, projectName, key, value);
    (ok ? pushed : failed).push(key);
  }
  return { pushed, failed };
}

export { slug };
